// useContactsStream — live GTM contact feed for the T5 Approvals queue (Chunk 3.5).
//
// Subscribes to GET {T6_BASE}/api/gtm/stream/contacts via a FETCH reader (same
// idiom as useCreditPoolStream — EventSource can't send the Firebase Bearer the
// /api/gtm admin gate requires). The server paints the current in-flight contacts
// on connect (stage:'snapshot'), then live-ticks enrichment/drafting stages and,
// when the deep-transcript recomposition lands, a superseding opener (stage:
// 'upgraded'). Every event carries the contact `id`, so we upsert by id: a later
// event for the same contact SUPERSEDES the card in place. No manual refresh.
//
// Returns { contacts, status }:
//   contacts — array of the latest merged event per contact, in first-seen order
//              so cards appear progressively and never reorder under the operator.
//   status   — connecting | live | reconnecting | error (same semantics as the
//              credit-pool stream; a dropped stream keeps the last contacts).

import { useCallback, useEffect, useRef, useState } from 'react';
import { authHeader, T6_BASE } from './useGtm';

const STREAM_PATH = '/api/gtm/stream/contacts';
const RETRY_MS = 3000; // server heartbeats every 25s; reconnect a beat after a drop

// Parse one SSE frame into its JSON data payload, or null (heartbeats/comments
// start with ':'; data may span multiple `data:` lines joined by \n).
function parseFrame(frame) {
  const dataLines = [];
  frame.split('\n').forEach((raw) => {
    const line = raw.replace(/\r$/, '');
    if (!line || line.startsWith(':')) return;
    if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  });
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }
}

export default function useContactsStream() {
  const [contacts, setContacts] = useState([]);
  const [status, setStatus] = useState('connecting');

  // First-seen order + latest merged event per id, held in a ref so the reconnect
  // loop mutates without re-subscribing the effect. We rebuild the array on change.
  const byId = useRef(new Map());
  const order = useRef([]);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  // Upsert one contact event by id (merge so a partial live event never blanks
  // fields a prior event carried), preserving first-seen order. Stable so the
  // page can call it to supersede a card locally (e.g. after a regenerate) even
  // when Redis/SSE isn't delivering.
  const applyLocal = useCallback((evt) => {
    if (!evt || !evt.id) return;
    const prev = byId.current.get(evt.id);
    if (!prev) order.current.push(evt.id);
    byId.current.set(evt.id, { ...(prev || {}), ...evt });
    setContacts(order.current.map((id) => byId.current.get(id)));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      setStatus((s) => (s === 'error' ? s : 'reconnecting'));
      timerRef.current = setTimeout(connect, RETRY_MS);
    };

    const connect = async () => {
      if (cancelled) return;
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const headers = await authHeader(); // fresh token each attempt
        const res = await fetch(`${T6_BASE}${STREAM_PATH}`, {
          headers: { ...headers, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) {
          if (!cancelled) setStatus('error');
          scheduleReconnect();
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let sep;
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const payload = parseFrame(buffer.slice(0, sep));
            buffer = buffer.slice(sep + 2);
            if (payload && payload.type === 'gtm_contact' && !cancelled) {
              applyLocal(payload);
              setStatus('live');
            }
          }
        }
        scheduleReconnect();
      } catch (err) {
        if (cancelled || (err && err.name === 'AbortError')) return;
        setStatus('error');
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // applyLocal is stable (useCallback []); listed to satisfy exhaustive-deps.
  }, [applyLocal]);

  return { contacts, status, applyLocal };
}
